import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import {
  PrismComboSelectInput,
  PrismLoadingBar,
  PrismModal,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';
import type { PmsVersion } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';

import styles from './pricing-assistant-upsell-modal.module.css';

interface PmsOption {
  id: string;
  label: string;
  pmsStatus: string;
}

interface HotelPricingAssistantModalsPricingAssistantUpsellModalSignature {
  Args: {
    closeModal: () => void;
  };
}

export default class HotelPricingAssistantModalsPricingAssistantUpsellModal extends Component<HotelPricingAssistantModalsPricingAssistantUpsellModalSignature> {
  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  @service
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];

  @tracked selectedPmsId;

  get isPATrialRequestLoading(): boolean {
    return this.pricingAssistantDataRequestService.startPAUpsellTrialTask
      .isRunning;
  }

  get isPmsDropdownDisabled(): boolean {
    return this.isPmsRequestLoading || this.access.hasOTB;
  }

  get isPmsRequestLoading(): boolean {
    return this.pricingAssistantDataRequestService.requestPmsTask.isRunning;
  }

  get isTrialButtonDisabled(): boolean {
    return (
      this.isPmsRequestLoading ||
      this.isPATrialRequestLoading ||
      !this.selectedPmsId
    );
  }

  get PmsComboSelectSubscript(): string {
    if (this.access.hasOTB) {
      return this.intl.t(
        'pricing_assistant.upsell_modal.comboselect_subscript_otb_integrated',
      );
    }
    return this.intl.t('pricing_assistant.upsell_modal.comboselect_subscript');
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pmsOptions() {
    if (!this.isPmsRequestLoading) {
      const options: PmsOption[] = [];

      const pmsVersions = this.store.peekAll<PmsVersion>('pms-version');

      // A PMS can have multiple versions. We want to show each version as a separate option in the dropdown (mainly important for Oracle Opera)
      pmsVersions.forEach((pmsVersion) => {
        const { pmsStatus } = pmsVersion;

        options.push({
          id: pmsVersion.id!,
          label: pmsVersion.displayName,
          pmsStatus,
        });
      });

      return options;
    }

    return [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedPmsOption() {
    if (!this.isPmsRequestLoading && this.selectedPmsId !== undefined) {
      return this.pmsOptions.find((option) => option.id === this.selectedPmsId);
    }

    return null;
  }

  constructor(
    owner: Owner,
    args: HotelPricingAssistantModalsPricingAssistantUpsellModalSignature['Args'],
  ) {
    super(owner, args);

    if (this.hotelAndMarketService.hotel.pmsVersionId) {
      this.selectedPmsId =
        this.hotelAndMarketService.hotel.pmsVersionId.toString();
    }

    void this.pricingAssistantDataRequestService.requestPmsTask.perform();
  }

  @action
  onSelectedPmsUpdate(pmsOption: PmsOption): void {
    this.selectedPmsId = pmsOption.id;
  }

  @action
  async startPAUpsellTrial(): Promise<void> {
    if (!this.selectedPmsOption) {
      return;
    }
    const result =
      await this.pricingAssistantDataRequestService.startPAUpsellTrialTask.perform(
        this.selectedPmsOption,
      );

    // Update feature flags so upsell banner FF can be updated and hidden
    await this.access.refetchFeatureFlags();

    this.args.closeModal();

    if (result.isSuccess) {
      let alertTitle;
      let alertText;

      // here we check if the sub has the FF "pricing_assistant.recommendations.view"
      // we check this so we can determine if the sub has full or limited access to PA
      // link to discussion https://otainsight.slack.com/archives/C04F5EM64C9/p1709567701615579?thread_ts=1709556910.288629&cid=C04F5EM64C9
      if (this.access.hasPricingAssistantViewRights) {
        alertTitle = this.intl
          .t('pricing_assistant.upsell_success_modal.title')
          .toString();
        alertText = this.intl
          .t('pricing_assistant.upsell_success_modal.text')
          .toString();
      } else {
        alertTitle = this.intl
          .t('pricing_assistant.upsell_otb_supported_success_modal.title', {
            pms: this.selectedPmsOption.label,
          })
          .toString();
        alertText = this.intl
          .t('pricing_assistant.upsell_otb_supported_success_modal.text')
          .toString();
      }

      const confirmWasClicked = await this.alerting.showSuccessAlert({
        title: alertTitle,
        text: alertText,
        buttons: {
          confirm: {
            text: this.intl.t('pricing_assistant.upsell_success_modal.button'),
          },
        },
      });

      if (confirmWasClicked) {
        // Redirect to Rates Pricing mode in case we have support and users clicks the CTA
        this.router.transitionTo('hotel.rates', {
          queryParams: { pageMode: 'pricing' },
        });
      }
    } else if (result.isPmsSupported === false) {
      await this.alerting.showErrorAlert({
        title: this.intl.t(
          'pricing_assistant.upsell_not_supported_modal.title',
          {
            pms: this.selectedPmsOption.label,
          },
        ),
        text: this.intl.t('pricing_assistant.upsell_not_supported_modal.text'),
        buttons: {
          confirm: {
            text: this.intl.t(
              'pricing_assistant.upsell_not_supported_modal.confirm_button',
            ),
          },
        },
      });
    }
  }

  <template>
    <PrismModal
      @onClose={{@closeModal}}
      @title={{t "pricing_assistant.upsell_modal.header_title"}}
    >
      <:default>
        <div class="modal--small modal--padded {{styles.upsell-modal}}">
          {{#if this.isPmsRequestLoading}}
            <PrismLoadingBar />
          {{/if}}
          <div class="flex flex-col gap-600">
            <div class="{{styles.emphasis-text}} flex flex-col gap-200">
              <h2>{{t "pricing_assistant.upsell_modal.title"}}</h2>
              <p class="text-300--regular">{{t
                  "pricing_assistant.upsell_modal.subtitle"
                }}</p>
            </div>
            <div class="flex flex-col gap-200">
              <div class="flex flex-col gap-100">
                <p class="text-200--bold {{styles.comboselect-label}}">{{t
                    "pricing_assistant.upsell_modal.comboselect_label"
                  }}</p>
                <PrismComboSelectInput
                  @disabled={{this.isPmsDropdownDisabled}}
                  @isSearchable={{true}}
                  @isSecondary={{true}}
                  @onChange={{this.onSelectedPmsUpdate}}
                  @options={{this.pmsOptions}}
                  @placeholder={{t
                    "pricing_assistant.upsell_modal.pms_dropdown_placeholder"
                  }}
                  @unsafe_matchTriggerWidth={{true}}
                  @value={{this.selectedPmsOption}}
                />
              </div>
              <p
                class="paragraph-200--regular"
              >{{this.PmsComboSelectSubscript}}</p>
            </div>
          </div>
        </div>
      </:default>
      <:footer>
        <div
          class="{{styles.footer}} flex h-full items-center justify-end pr-500"
        >
          {{#if this.isPATrialRequestLoading}}
            <PrismLoadingBar />
          {{/if}}

          <GeneralFormFieldsCancelAndConfirm
            @confirm={{this.startPAUpsellTrial}}
            @confirmText={{t "pricing_assistant.upsell_modal.confirm_button"}}
            @hasNoCancelButton={{true}}
            @isConfirmDisabled={{this.isTrialButtonDisabled}}
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
